/**
 * QORYN GTM Command Center — /gtm page.
 *
 * Read-oriented dashboard. Reads via supabase-js (RLS SELECT) + realtime
 * subscription; founder-only writes flow through /api/gtm/status.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import {
    fetchMilestonesSupabase,
    fetchEventsSupabase,
    fetchMe,
    subscribeGtm,
} from "@/lib/gtm";
import {
    groupByPhase,
    summaryMetrics,
    PHASE_TITLES,
} from "@/pages/gtm/progressCalc";
import SummaryTiles from "@/pages/gtm/SummaryTiles";
import FiltersBar from "@/pages/gtm/FiltersBar";
import RoadmapRail from "@/pages/gtm/RoadmapRail";
import FounderRail from "@/pages/gtm/FounderRail";
import DomainReadinessPanel from "@/pages/gtm/DomainReadiness";
import RecentActivity from "@/pages/gtm/RecentActivity";
import StatusUpdateModal from "@/pages/gtm/StatusUpdateModal";

const FILTER_MATCHERS = {
    ALL: () => true,
    ACTIVE: (m) => ["IN_PROGRESS", "FOUNDER_REVIEW", "WAITING_EXTERNAL"].includes(m.status),
    BLOCKERS: (m) => m.status === "BLOCKED",
    LAUNCH: (m) => m.launch_critical && !["COMPLETE", "COMPLETE_WITH_EXCEPTION"].includes(m.status),
    COMPLETED: (m) => ["COMPLETE", "COMPLETE_WITH_EXCEPTION"].includes(m.status),
};

export default function GTMCommandCenter() {
    const [milestones, setMilestones] = useState([]);
    const [events, setEvents] = useState([]);
    const [meLoading, setMeLoading] = useState(true);
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState("ALL");
    const [launchLensOpen, setLaunchLensOpen] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorMilestone, setEditorMilestone] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const [ms, ev] = await Promise.all([
                fetchMilestonesSupabase(),
                fetchEventsSupabase({ limit: 30 }),
            ]);
            setMilestones(ms);
            setEvents(ev);
            setError(null);
        } catch (err) {
            setError(err?.message ?? String(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        fetchMe()
            .then((r) => setMe(r))
            .catch(() => setMe({ is_founder: false }))
            .finally(() => setMeLoading(false));
        const unsub = subscribeGtm({
            onMilestone: () => refresh(),
            onEvent: () => refresh(),
        });
        return unsub;
    }, [refresh]);

    const summary = useMemo(() => summaryMetrics(milestones), [milestones]);

    // Base filter (roadmap view) — the launch-lens is a separate mode that
    // overrides the base filter.
    const roadmapMilestones = useMemo(() => {
        if (launchLensOpen) {
            const lc = milestones.filter(
                (m) => m.launch_critical && !["COMPLETE", "COMPLETE_WITH_EXCEPTION"].includes(m.status),
            );
            return lc;
        }
        const match = FILTER_MATCHERS[filter] ?? (() => true);
        return milestones.filter(match);
    }, [milestones, filter, launchLensOpen]);

    const phasesGrouped = useMemo(() => groupByPhase(roadmapMilestones), [roadmapMilestones]);

    const openEditor = useCallback((m) => {
        setEditorMilestone(m);
        setEditorOpen(true);
    }, []);

    const onSaved = useCallback(async () => {
        await refresh();
    }, [refresh]);

    // When launch lens is on, auto-expand every phase.
    const initiallyExpandedPhases = useMemo(() => {
        if (launchLensOpen) return phasesGrouped.map(([p]) => p);
        // Otherwise: default-expand STAGE_B_GOVERNANCE + PHASE_1_FOUNDATION only.
        return ["STAGE_B_GOVERNANCE", "PHASE_1_FOUNDATION"];
    }, [launchLensOpen, phasesGrouped]);

    const launchCriticalCount = summary.launch_critical_remaining;

    return (
        <div data-testid="gtm-command-center" className="space-y-6">
            <header className="space-y-1">
                <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-qoryn-cyan/80">
                    QORYN
                </div>
                <h1 className="font-display text-[32px] sm:text-[36px] font-light tracking-tight text-qoryn-text">
                    GO-TO-MARKET COMMAND CENTER
                </h1>
                <p className="font-sans text-[13px] text-qoryn-textDim">
                    Roadmap to market. Summary of authoritative work — never a substitute for it.
                </p>
            </header>

            {loading && (
                <div data-testid="gtm-loading" className="qoryn-panel rounded-2xl p-8 flex items-center justify-center text-qoryn-textDim">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    <span className="font-mono-ui text-[10px] tracking-widest uppercase">Loading roadmap…</span>
                </div>
            )}

            {error && (
                <div data-testid="gtm-error" className="qoryn-panel rounded-2xl p-4 border-red-500/40 bg-red-500/[0.04] text-red-300 flex items-start gap-3">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={1.75} />
                    <div>
                        <div className="font-mono-ui text-[10px] tracking-widest uppercase">Load failed</div>
                        <div className="text-[12.5px] mt-1">{String(error)}</div>
                    </div>
                </div>
            )}

            {!loading && !error && (
                <>
                    <SummaryTiles summary={summary} />

                    <FiltersBar
                        value={filter}
                        onChange={(v) => { setLaunchLensOpen(false); setFilter(v); }}
                        onLaunchLens={() => setLaunchLensOpen((v) => !v)}
                    />

                    {launchLensOpen && (
                        <div data-testid="gtm-launch-lens" className="qoryn-panel rounded-2xl p-4 border-qoryn-cyan/40 bg-qoryn-cyan/[0.04]">
                            <div className="font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-cyan flex items-center gap-2">
                                <span>{launchCriticalCount} Launch-Critical Milestones Remaining</span>
                            </div>
                            <div className="mt-1 text-[12.5px] text-qoryn-textDim">
                                Filtered to milestones that are incomplete AND launch-critical, in dependency order.
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
                        <RoadmapRail
                            phasesGrouped={phasesGrouped}
                            isFounder={me?.is_founder}
                            onEdit={openEditor}
                            initiallyExpandedPhases={initiallyExpandedPhases}
                        />
                        <aside className="min-w-0">
                            <FounderRail milestones={milestones} />
                        </aside>
                    </div>

                    <DomainReadinessPanel milestones={milestones} />
                    <RecentActivity events={events} milestones={milestones} />

                    <div className="pt-4 pb-2 text-center font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                        Dashboard status ≠ certification authority. This console summarizes governed work.
                    </div>
                </>
            )}

            <StatusUpdateModal
                open={editorOpen}
                onOpenChange={setEditorOpen}
                milestone={editorMilestone}
                onSaved={onSaved}
            />
        </div>
    );
}
